import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { CheckCircle, ExpandMore, TrendingUp, Psychology, NetworkCheck, Code, Speed, EmojiEvents } from '@mui/icons-material';

const SweLandJobPage: React.FC = () => {

  const painPoints = [
    "You're sending hundreds of applications but getting zero responses",
    "Every rejection feels like a personal failure and you're losing motivation",
    "You don't know what to study or how to prioritize your limited prep time",
    "Technical interviews feel like a guessing game where you're always unprepared",
    "Your resume disappears into the ATS black hole and never reaches human eyes",
    "You lack the confidence to negotiate offers because you don't understand your value"
  ];

  const benefits = [
    {
      icon: <NetworkCheck sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Build a Network That Works for You',
      description: 'Transform your social media presence into a lead generation machine that brings opportunities to your inbox'
    },
    {
      icon: <Psychology sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Develop Unshakeable Confidence',
      description: 'Master the mindset techniques that turn rejection into fuel and setbacks into comebacks'
    },
    {
      icon: <Code sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Dominate All Interview Types',
      description: 'Master the 4 Horsemen of technical interviews with proven frameworks and daily practice systems'
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Accelerate Your Timeline',
      description: 'Skip years of trial and error with a proven 90-day roadmap that delivers results'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Optimize Every Touchpoint',
      description: 'From ATS-beating resumes to compelling behavioral stories, perfect every part of your candidacy'
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Land Multiple Offers',
      description: 'Position yourself as the obvious choice and negotiate from a position of strength'
    }
  ];

  const includedItems = [
    {
      title: 'Social Media & Networking Mastery',
      description: 'LinkedIn optimization secrets, recruiter DM templates, and community building strategies that generate inbound opportunities'
    },
    {
      title: 'Rejection-Proof Mindset System',
      description: 'Science-backed techniques including meditation practices that rewire your brain for resilience and sustained motivation'
    },
    {
      title: 'Environment Optimization Blueprint',
      description: 'How to structure your life, find study partners, and create focus zones that accelerate your learning curve'
    },
    {
      title: 'The 4 Horsemen Technical Mastery',
      description: 'Complete frameworks for trivia, take-home projects, DSA/coding problems, and system design with the proven Rule of 100 practice system'
    },
    {
      title: 'ATS-Beating Resume Secrets',
      description: 'Keyword optimization, metrics-driven formatting, and prestige positioning strategies that get you past the robots and into human hands'
    },
    {
      title: 'Behavioral Interview Story Bank',
      description: '10-15 compelling narratives using the STAR method, mock interview strategies, and negotiation techniques for multiple offers'
    }
  ];

  const faqs = [
    {
      question: "Is 90 days really realistic in today's tough job market?",
      answer: "This system was developed during challenging market conditions and has worked for engineers at all levels. The 90-day timeline assumes consistent daily effort using the Rule of 100 framework. Some land jobs faster, others need additional time, but the systematic approach dramatically improves your odds regardless of market conditions."
    },
    {
      question: "What if I don't have enough experience or projects?",
      answer: "The course includes specific strategies for building impressive projects, including working with real local businesses for maximum prestige. You'll learn how to position your existing experience effectively and build new experiences that demonstrate real-world impact."
    },
    {
      question: "I've tried networking before and it didn't work. How is this different?",
      answer: "Most people network wrong - they ask for favors instead of providing value. This system teaches you to build genuine relationships through platforms like Twitch, YouTube, and LinkedIn by sharing your journey and helping others. The result is organic mentorship and referrals."
    },
    {
      question: "How much time do I need to dedicate daily?",
      answer: "The Rule of 100 framework requires 100 minutes per day for focused practice. This can be broken into smaller chunks throughout the day. The key is consistency over intensity - sustainable daily progress beats sporadic marathon sessions."
    },
    {
      question: "What if I'm not good at system design or advanced topics?",
      answer: "The course breaks down system design into manageable levels (high-level, low-level, OOP design) with specific book and YouTube channel recommendations. You'll learn to tackle complex topics incrementally using the 'Elo-based' approach for steady skill building."
    },
    {
      question: "Can I really negotiate multiple offers if I've never done it before?",
      answer: "The behavioral interview section includes specific negotiation strategies and frameworks. When you follow the system, you'll naturally generate multiple opportunities, giving you the leverage needed for successful negotiations."
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>

      <Box sx={{ pt: 12, pb: 8 }}>
        <Container maxWidth="md">
          {/* Audience Callout */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h6"
              sx={{ color: 'secondary.main', mb: 2, fontWeight: 'bold' }}
            >
              For Aspiring Software Engineers Tired of Endless Applications and Rejections
            </Typography>
          </Box>

          {/* Bold Promise */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{ color: 'text.primary', mb: 3, fontWeight: 'bold' }}
            >
              <span style={{ color: '#88C0D0' }}>Land Your Software Engineering Job</span> in 90 Days
            </Typography>
          </Box>

          {/* Summary */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h5"
              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.4 }}
            >
              Get instant access to the complete 2-hour video course with social media networking mastery, rejection-proof mindset training, environment optimization blueprint, the 4 Horsemen technical interview system, ATS-beating resume secrets, and the behavioral interview story bank framework that transforms job searches into job offers.
            </Typography>
          </Box>

          {/* CTA Buttons */}
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.location.href = '/checkout?product=swe-job-course'}
                sx={{
                  color: 'black',
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  '&:hover': { transform: 'scale(1.02)' },
                  transition: 'transform 0.2s',
                }}
              >
                Get Complete Course for $11
              </Button>
              <Button
                component="a"
                href="https://www.skool.com/raymond/about"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  borderWidth: 2,
                  '&:hover': { 
                    borderWidth: 2,
                    transform: 'scale(1.02)' 
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get It FREE in $7 Community
              </Button>
            </Box>
          </Box>

          {/* Pain Points */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6 }}
            >
              If Any of This <span style={{ color: '#88C0D0' }}>Sounds Like You...</span>
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {painPoints.map((point, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    bgcolor: 'rgba(136, 192, 208, 0.05)', 
                    border: '1px solid #333',
                    '&:hover': {
                      border: '1px solid #88C0D0',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                      ❌ {point}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Benefits */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6 }}
            >
              Transform Into a <span style={{ color: '#88C0D0' }}>Job Landing Machine</span>
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
              {benefits.map((benefit, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    bgcolor: 'background.paper', 
                    border: '2px solid #333',
                    '&:hover': {
                      border: '2px solid #88C0D0',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      <Box sx={{ flexShrink: 0 }}>
                        {benefit.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 'bold' }}>
                          {benefit.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          {benefit.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Solution Introduction */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: 'text.primary', mb: 4 }}
            >
              Introducing <span style={{ color: '#88C0D0' }}>Land a SWE Job in 90 Days</span>
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.5 }}
            >
              The battle-tested roadmap that transforms job search confusion into systematic success with proven frameworks and real-world strategies.
            </Typography>
          </Box>

          {/* Solution Discovery Story */}
          <Card sx={{ bgcolor: 'background.paper', border: '2px solid #333', mb: 8 }}>
            <CardContent sx={{ p: 6 }}>
              <Typography variant="h5" sx={{ color: 'secondary.main', mb: 4, textAlign: 'center' }}>
                How This System Was Born From Real Success
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, lineHeight: 1.8 }}>
                This isn't theoretical advice from someone who's never been in the trenches. This system was forged through years of painful rejections, countless failed applications, and the eventual breakthrough that landed a software engineering offer at a major company.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, lineHeight: 1.8 }}>
                After analyzing what worked versus what didn't, I discovered that successful job searches aren't about luck or perfect timing—they're about executing proven systems across six critical areas. Most people fail because they focus on just one area (usually coding) while ignoring the other five.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, lineHeight: 1.8 }}>
                I spent months reverse-engineering every successful hire I could find, studying their strategies, and testing what worked in different market conditions. The patterns were clear: those who succeeded followed specific frameworks for networking, mindset, technical preparation, and presentation.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.8 }}>
                The result? A systematic 90-day roadmap that removes guesswork and provides clear daily actions. This isn't just my story—it's become the foundation for hundreds of successful job searches, each validating and refining the system further.
              </Typography>
            </CardContent>
          </Card>

          {/* What's Included */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6 }}
            >
              What's <span style={{ color: '#88C0D0' }}>Included</span>
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr' }, gap: 3 }}>
              {includedItems.map((item, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    bgcolor: 'rgba(136, 192, 208, 0.1)', 
                    border: '2px solid #88C0D0',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      <CheckCircle sx={{ color: 'secondary.main', flexShrink: 0, mt: 0.5 }} />
                      <Box>
                        <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 'bold' }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* FAQ */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6 }}
            >
              Frequently Asked <span style={{ color: '#88C0D0' }}>Questions</span>
            </Typography>
            
            {faqs.map((faq, index) => (
              <Accordion 
                key={index}
                sx={{ 
                  bgcolor: 'background.paper', 
                  border: '1px solid #333',
                  mb: 2,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: 'secondary.main' }} />}
                  sx={{ 
                    '&:hover': { bgcolor: 'rgba(136, 192, 208, 0.05)' }
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Final CTA */}
          <Paper
            sx={{
              p: 6,
              bgcolor: 'rgba(136, 192, 208, 0.1)',
              border: '3px solid #88C0D0',
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{ 
                color: 'secondary.main', 
                mb: 4, 
                fontWeight: 'bold',
                fontSize: '1.2rem',
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              Stop Wishing, Start Landing Jobs
            </Typography>
            
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: 'text.primary', mb: 4 }}
            >
              Get Your <span style={{ color: '#88C0D0' }}>90-Day Roadmap</span> Today
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.location.href = '/checkout?product=swe-job-course'}
                sx={{
                  color: 'black',
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  '&:hover': { transform: 'scale(1.02)' },
                  transition: 'transform 0.2s',
                }}
              >
                Get Complete Course for $11
              </Button>
              <Button
                component="a"
                href="https://www.skool.com/raymond/about"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  py: 2,
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  borderWidth: 2,
                  '&:hover': { 
                    borderWidth: 2,
                    transform: 'scale(1.02)' 
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get It FREE in $7 Community
              </Button>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Join engineers who've transformed their careers with this proven system
            </Typography>
          </Paper>

          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
              Recent Job Landing Success Stories
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: 'secondary.main' }} />
                <Typography sx={{ color: 'text.primary' }}>Alex → Microsoft ($195k)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: 'secondary.main' }} />
                <Typography sx={{ color: 'text.primary' }}>Jordan → Stripe ($240k)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: 'secondary.main' }} />
                <Typography sx={{ color: 'text.primary' }}>Taylor → Airbnb ($220k)</Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SweLandJobPage;